import { useCallback, useState } from 'react';
import {
  useGetMeQuery,
  useMarkSmsAlertsIntroSeenMutation,
} from 'store/services/user/meApi';

export interface UseSmsAlertsIntroGateResult {
  /** True when the first-time SMS Alerts intro modal should be displayed. */
  isModalOpen: boolean;
  /**
   * Wrap an SMS-alert-enabling action behind the one-time consent modal.
   *
   * If the user has already seen the intro, `proceed` runs immediately.
   * Otherwise the intro modal opens and `proceed` is deferred — it runs only
   * when the user confirms via the CTA, and never on dismiss. The real enable
   * mutation lives inside `proceed`, so nothing fires (and the bell/toggle
   * never flickers) until the user gives consent.
   */
  guardEnable: (proceed: () => void) => void;
  /** "Enable SMS Alerts" CTA — marks the intro seen and runs the deferred action. */
  handleConfirm: () => void;
  /** Dismiss / backdrop / Escape — marks the intro seen and discards the action. */
  handleDismiss: () => void;
}

/**
 * Gates SMS-alert-enabling actions behind a one-time educational/consent modal.
 *
 * Instantiate one per screen that can enable alerts (Have List, Settings). The
 * shared gate is the `hasSeenSmsAlertsIntro` flag on the `/me` cache, so once
 * any entry point marks it seen, no entry point shows the modal again.
 */
export function useSmsAlertsIntroGate(): UseSmsAlertsIntroGateResult {
  const { data: user } = useGetMeQuery();
  const [markSeen] = useMarkSmsAlertsIntroSeenMutation();
  // Holds the deferred enable action while the modal is open. The modal is
  // open iff this is non-null, so the two can never disagree.
  const [pendingProceed, setPendingProceed] = useState<(() => void) | null>(null);

  // `undefined` (while /me loads) is treated as "not seen" — showing the intro
  // an extra time is far safer than enabling SMS without consent.
  const hasSeenIntro = user?.hasSeenSmsAlertsIntro === true;

  const guardEnable = useCallback(
    (proceed: () => void) => {
      if (hasSeenIntro) {
        proceed();
        return;
      }
      // Defer the enable action; setting it open the modal.
      setPendingProceed(() => proceed);
    },
    [hasSeenIntro],
  );

  const handleConfirm = useCallback(() => {
    const proceed = pendingProceed;
    setPendingProceed(null);
    void markSeen();
    proceed?.();
  }, [pendingProceed, markSeen]);

  const handleDismiss = useCallback(() => {
    // Discard the pending action — dismissal means "not ready to enable".
    setPendingProceed(null);
    void markSeen();
  }, [markSeen]);

  return {
    isModalOpen: pendingProceed !== null,
    guardEnable,
    handleConfirm,
    handleDismiss,
  };
}
